import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ExpectedBreakdownSpec } from './tenderMath'

/**
 * "Where does 165,935.57 come from?" — the arithmetic behind an expected
 * figure, closed by default. On the count steps this replaces the long
 * "Expected … — … today + … earlier orders" sentence and the amber cross-day
 * callout; on the review table it sits under each row (F-FE-N139).
 */
export function ExpectedBreakdown({
  summary,
  spec,
  formatCurrency,
  className,
}: {
  summary: string
  spec: ExpectedBreakdownSpec
  formatCurrency: (n: number) => string
  className?: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 py-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronDown
          className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
        {summary}
      </button>
      {open && (
        <div className="mt-2 space-y-1.5 rounded-lg bg-muted/40 px-4 py-3 text-sm">
          {spec.rows.map((r, i) => (
            <div
              key={i}
              className={cn(
                'flex items-baseline justify-between gap-4',
                r.tone === 'muted' && 'text-muted-foreground',
              )}
            >
              <span>
                {r.sign ? `${r.sign} ` : ''}
                {r.label}
              </span>
              <span className="tabular-nums font-medium">{formatCurrency(r.amount)}</span>
            </div>
          ))}
          <div className="flex items-baseline justify-between gap-4 border-t pt-1.5 font-semibold">
            <span>{spec.total.label}</span>
            <span className="tabular-nums">{formatCurrency(spec.total.amount)}</span>
          </div>
          {spec.why && <p className="pt-1 text-xs text-muted-foreground">{spec.why}</p>}
        </div>
      )}
    </div>
  )
}
